/**
 * Radio Options AST Node
 *
 * A content block for single-choice radio button questions.
 * Appears in the content flow alongside lines and tables.
 * Participates in validation via name/answer.
 */

import { z } from 'zod';
import { AST_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Radio Options Block
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Radio options block schema - single-choice radio buttons
 *
 * @example
 * {
 *   type: "RadioOptions",
 *   name: "q1",
 *   options: ["apple", "orange", "banana"],
 *   answer: "apple"
 * }
 */
export const RadioOptionsBlockSchema = z.object({
  type: z.literal(AST_TYPES.RADIO_OPTIONS),
  name: z.string(),
  options: z.array(z.string()).min(2),
  answer: z.string()
});

export type RadioOptionsBlock = z.infer<typeof RadioOptionsBlockSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Factory Function
// ═══════════════════════════════════════════════════════════════════════════

export function createRadioOptions(name: string, options: string[], answer: string): RadioOptionsBlock {
  return RadioOptionsBlockSchema.parse({
    type: AST_TYPES.RADIO_OPTIONS,
    name,
    options,
    answer
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guard
// ═══════════════════════════════════════════════════════════════════════════

export function isRadioOptions(value: unknown): value is RadioOptionsBlock {
  return RadioOptionsBlockSchema.safeParse(value).success;
}
